import type { Design, DesignRecord } from '@noorview/core';
import { activeDesign, blankDesign } from '@noorview/core';
import type { Workspace } from '../data/backend';

const cache = new WeakMap<DesignRecord, Design>();

/** What a screen is showing right now: its published design, or a scheduled one while a schedule runs. */
export function liveDesign(ws: Workspace, screenId: string, now: number): Design | null {
  const screen = ws.screens.find((s) => s.id === screenId);
  const rec = screen && ws.designs.find((d) => d.id === screen.designId);
  if (!screen || !rec?.published) return null;
  const schedules = ws.schedules.filter((s) => s.screenId === screenId).flatMap((s) => {
    const d = ws.designs.find((x) => x.id === s.designId)?.published;
    return d ? [{ ...s, design: d }] : [];
  });
  if (!schedules.length) return stable(rec);
  return activeDesign({ design: rec.published, schedules }, new Date(now)).design;
}

/** Same object for the same record, so React doesn't re-render previews needlessly. */
function stable(rec: DesignRecord): Design {
  let d = cache.get(rec);
  if (!d) { d = rec.published!; cache.set(rec, d); }
  return d;
}

export const designOf = (ws: Workspace, screenId: string) => {
  const s = ws.screens.find((x) => x.id === screenId);
  return s ? ws.designs.find((d) => d.id === s.designId) ?? null : null;
};

/**
 * The Studio's own blank canvas: what the Studio opens from the menu, not linked to any screen. Solid black with the
 * grid dots showing, whatever the app's theme; it keeps what's designed on it until Clear. Send Live asks which screen.
 */
export const CANVAS_NAME = 'Studio canvas';
export const canvasOf = (ws: Workspace): DesignRecord | null =>
  ws.designs.find((d) => d.name === CANVAS_NAME && !ws.screens.some((s) => s.designId === d.id) && !ws.schedules.some((s) => s.designId === d.id)) ?? null;
export function blankCanvas(): Design {
  const d = blankDesign('16:9');
  return { ...d, background: { kind: 'solid', color: '#000000' } };
}
